import React, { FC, useContext, useState } from 'react'

// Libs
import { LockKeyhole } from 'lucide-react'

// Components
import { BankAccountSetup } from './components/BankAccountSetupForm'
import MainModal from './common/MainModal'
import { Checkbox } from '../ui/checkbox'
import { Label } from '../ui/label'
import { Button } from '../ui/button'

// Helpers
import { HandleModalContext } from '../../layout/mainlayout/MainLayout'

// Types
import { IUserInterface } from '../../models/users/user.interface'

import { MODAL_CONSTS } from '../../const/modal-const'
import { httpService } from '../../services/httpService'
import { MESSAGES_CONST } from '../../const/messages-const'
import useToasterHelper from '../../helpers/ToasterHelper'

interface ConnectBankAccountProps {
  dataToPassOn: {
    reOpenModalName: string
    user: IUserInterface
    organizer: IUserInterface
    // "isApproved" is true, then we must provide access to the user "ACH" if the "ACH" is added by the organizer
    isApproved?: boolean
    onSave?: (customerId: string) => Promise<void>
  }
}

export const ConnectBankAccount: FC<ConnectBankAccountProps> = ({ dataToPassOn }) => {
  const toasterFunctions = useToasterHelper()

  const handleModal = useContext(HandleModalContext)?.handleModal

  const { user, organizer, reOpenModalName, onSave } = dataToPassOn

  const [isAgree, setIsAgree] = useState(false)
  const [clientSecret, setClientSecret] = useState('')

  const closeModal = () => {
    if (reOpenModalName) handleModal?.(true, reOpenModalName, dataToPassOn)

    handleModal?.(false, MODAL_CONSTS.CONNECT_BANK_ACCOUNT, dataToPassOn)
  }

  const createBankSetup = async () => {
    try {
      const { setupIntent } = await httpService({
        url: 'create-bank-setup',
        method: 'POST',
        data: {
          userEmail: user.userEmail,
          userStripeAccountId: organizer?.userStripeAccountId,
        },
      })

      setClientSecret(setupIntent.client_secret)
    } catch (error: any) {
      console.error(error)
      toasterFunctions.error({
        message: error?.response?.data?.message || MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  return (
    <MainModal
      title="Connect bank account"
      show={true}
      type="CONNECT_BANK_ACCOUNT"
      size="md"
      className="flex flex-col flex-1"
      onHide={closeModal}
    >
      <div className="flex flex-col flex-1 w-full pt-4 h-full text-SeabiscuitDark200ThemeColor">
        <div className="bg-black/[2%] py-4 px-5 rounded-2xl mb-6">
          <div className="pl-10 relative">
            <LockKeyhole className="w-5 absolute z-10 left-0 top-0" />
            <p className="text-sm mb-7 leading-normal">
              By providing your bank account information and confirming this payment, you authorize
              Pegasus to debit your account for payments in accordance with our terms.
            </p>
            <p className="text-sm leading-normal">
              You may revoke this authorization at any time by removing your bank account from your
              Pegasus payment settings, which will immediately cancel our authorization to debit
              your account.
            </p>
          </div>
        </div>
        <Label className="text-sm font-normal relative px-5 items-start">
          <Checkbox
            checked={isAgree}
            onCheckedChange={async (value) => {
              setIsAgree(value as boolean)
              if (value) {
                await createBankSetup()
              } else {
                setClientSecret('')
              }
            }}
            className="relative top-1"
          />
          <span className="pl-4 cursor-pointer leading-normal">
            By checking this box, I confirm I have read and agree to the ACH authorization terms and
            authorize Pegasus to debit my bank account as described.
          </span>
        </Label>
        <div className="!mt-auto">
          <div className="mt-10 flex flex-wrap items-start justify-between gap-3">
            {isAgree && (
              <BankAccountSetup
                user={user}
                organizer={organizer}
                userStripeAccountId={organizer.userStripeAccountId || ''}
                clientSecret={clientSecret}
                closeModal={closeModal}
                onSave={onSave}
                isApproved={dataToPassOn.isApproved || false}
              />
            )}
            <Button
              variant="secondary"
              className="w-full text-base font-normal py-3.5 rounded-[10px]"
              onClick={closeModal}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </MainModal>
  )
}
